import {useEffect, useMemo, useState} from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  Box,
  Button,
  VStack,
  HStack,
  Text,
  Badge,
  Icon,
  useColorModeValue,
  useBreakpointValue,
} from '@chakra-ui/react';
import {TablerModalCloseButton as ModalCloseButton} from './TablerCloseButtons';
import {
  TablerMicrophone,
  TablerGlobe,
  TablerChevronLeft,
  TablerChevronRight,
  TablerClock,
} from '../../icons';
import LanguageDialectPicker from './LanguageDialectPicker';
import {
  LIVE_ENCOUNTER_WORKFLOW_TYPE,
  TELEHEALTH_ENCOUNTER_WORKFLOW_TYPE,
} from '../../services/encounterUtils';

// ---------------------------------------------------------------------------
// ContinueModePickerModal
//
// Shown when a provider taps "Continue encounter" on a closed in-person
// encounter. It asks a single question — do you want the interpreter on for
// this next session? — and links the new Conversation into the same Encounter
// either way.
//
//   - Interpreter on  → a live translation Encounter (`encounter` mode).
//   - Interpreter off → a Scribe-only session (`scribe` mode).
//
// (The on/off framing maps onto the two existing continuation modalities, so
// the caller contract — `onSelect('encounter' | 'scribe')` — is unchanged.)
//
// The option matching the encounter's most recent session is flagged with a
// "Last used" badge so a provider can one-tap continue the same way.
//
// Two-step flow: when the encounter has no saved interpretation language and
// the provider picks "Interpreter on" (`liveNeedsLanguage`), the modal advances
// to a second step that asks for the language (+ optional dialect) instead of
// dead-ending on a "go set it from Home" toast. The caller persists the choice
// and continues once `onSelect('encounter', {language, dialect})` fires.
// Encounters that already have a language skip the second step entirely.
//
// Props:
//   isOpen            Controlled open state.
//   encounter         EncounterView being continued (derives "Last used").
//   scribeDisabled    When true, the "interpreter off" option shows disabled.
//   liveNeedsLanguage When true, picking "Interpreter on" opens the language
//                     step; the caller supplies this from the encounter's
//                     missing-language state.
//   defaultLanguage   Pre-fills the language step (e.g. patient preference).
//   defaultDialect    Pre-fills the dialect on the language step.
//   onSelect(mode, extra?)  mode is 'scribe' | 'encounter'. For a freshly
//                     picked language, extra is {language, dialect}.
//   onClose           Dismiss without choosing.
// ---------------------------------------------------------------------------

const LIVE_ENCOUNTER_MODE = 'encounter';
const LIVE_ENCOUNTER_WORKFLOWS = new Set([
  LIVE_ENCOUNTER_WORKFLOW_TYPE,
  TELEHEALTH_ENCOUNTER_WORKFLOW_TYPE,
]);

// Minimal Firestore-Timestamp / Date / seconds-shape -> epoch-ms coercion so
// this presentational modal stays free of the encounterService module graph.
function tsToMs(value: any) {
  if (!value) return 0;
  if (typeof value.toDate === 'function') {
    try {
      return value.toDate().getTime();
    } catch {
      return 0;
    }
  }
  if (typeof value.seconds === 'number') return value.seconds * 1000;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

function workflowToMode(workflowType: any) {
  return LIVE_ENCOUNTER_WORKFLOWS.has(workflowType || '')
    ? LIVE_ENCOUNTER_MODE
    : 'scribe';
}

const ContinueModePickerModal = ({
  isOpen,
  encounter = null,
  scribeDisabled = false,
  liveNeedsLanguage = false,
  defaultLanguage = '',
  defaultDialect = '',
  onSelect,
  onClose,
}: Record<string, any>) => {
  const motionPreset = useBreakpointValue({base: 'slideInBottom', md: 'scale'});
  const cardBg = useColorModeValue('white', 'gray.800');
  const optionBg = useColorModeValue('white', 'gray.700');
  const optionHoverBg = useColorModeValue('gray.50', 'gray.600');
  const titleColor = useColorModeValue('gray.900', 'white');
  const subtleText = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const chevronColor = useColorModeValue('gray.400', 'gray.500');

  // Each option gets a soft, single-hue icon tile so the two modes read
  // distinctly at a glance — opalite blue for the interpreter (translation)
  // and a calm green for the scribe (recording a note). Kept to the 50/600
  // tints (light) and low-alpha brand washes (dark) so it stays a quiet
  // accent rather than a loud color chooser.
  const interpreterTileBg = useColorModeValue(
    'opalite.50',
    'rgba(30,159,212,0.16)',
  );
  const interpreterTileColor = useColorModeValue('opalite.600', 'opalite.200');
  const scribeTileBg = useColorModeValue('green.50', 'rgba(72,187,120,0.16)');
  const scribeTileColor = useColorModeValue('green.600', 'green.200');
  const lastUsedBg = useColorModeValue('gray.100', 'whiteAlpha.200');
  const lastUsedColor = useColorModeValue('gray.700', 'gray.300');
  const lastUsedBorder = useColorModeValue('gray.200', 'whiteAlpha.300');
  const hoverBorder = useColorModeValue('gray.300', 'gray.500');
  const focusRing = 'var(--chakra-colors-gray-400)';
  const patientAccent = useColorModeValue('gray.900', 'white');

  const patientName = (encounter?.patient_name || '').trim();

  // Two-step state. `mode` is the modality picker; `language` is the language
  // capture shown only when the provider picks "Interpreter on" for an
  // encounter that never saved a language. Reset every time the modal (re)opens
  // so a dismissed-and-reopened picker always starts on the modality step with
  // the caller's latest defaults.
  const [step, setStep] = useState<'mode' | 'language'>('mode');
  const [language, setLanguage] = useState(defaultLanguage || '');
  const [dialect, setDialect] = useState(defaultDialect || '');
  useEffect(() => {
    if (isOpen) {
      setStep('mode');
      setLanguage(defaultLanguage || '');
      setDialect(defaultDialect || '');
    }
  }, [isOpen, defaultLanguage, defaultDialect]);

  // Picking a modality: "Interpreter on" for a language-less encounter advances
  // to the language step; everything else resolves immediately.
  const handleOptionSelect = (mode: any) => {
    if (mode === LIVE_ENCOUNTER_MODE && liveNeedsLanguage) {
      setStep('language');
      return;
    }
    onSelect?.(mode);
  };

  const handleStartWithLanguage = () => {
    const chosen = (language || '').trim();
    if (!chosen) return;
    onSelect?.(LIVE_ENCOUNTER_MODE, {
      language: chosen,
      dialect: (dialect || '').trim(),
    });
  };

  // Modality of the encounter's most recent session. Prefer the latest member
  // Conversation's own workflow_type (handles mixed-modality encounters);
  // fall back to the encounter doc's workflow_type when no member
  // conversations are visible (e.g. a cross-team encounter).
  const lastUsedMode = useMemo(() => {
    const convs = Array.isArray(encounter?.conversations)
      ? encounter.conversations
      : [];
    if (convs.length) {
      const latest = [...convs].sort(
        (a, b) => tsToMs(b?.start_time) - tsToMs(a?.start_time),
      )[0];
      const wf = latest?.workflow_type || latest?.workflowType || '';
      if (wf) return workflowToMode(wf);
    }
    const encWf = encounter?.workflow_type || encounter?.workflowType || '';
    return encWf ? workflowToMode(encWf) : null;
  }, [encounter]);

  const renderOption = ({
    mode,
    title,
    description,
    icon,
    disabled,
    isLastUsed,
    tileBg,
    tileColor,
  }: Record<string, any>) => (
    <Box
      as="button"
      type="button"
      w="100%"
      textAlign="left"
      bg={optionBg}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="xl"
      px={5}
      py={4.5}
      minH="78px"
      cursor={disabled ? 'not-allowed' : 'pointer'}
      opacity={disabled ? 0.6 : 1}
      transition="all 0.15s ease"
      _hover={
        disabled
          ? undefined
          : {
              bg: optionHoverBg,
              borderColor: hoverBorder,
              transform: 'none',
            }
      }
      _active={disabled ? undefined : {transform: 'translateY(0)'}}
      _focusVisible={{
        boxShadow: `0 0 0 3px ${focusRing}`,
        outline: 'none',
        borderColor: hoverBorder,
      }}
      onClick={() => {
        if (!disabled) handleOptionSelect(mode);
      }}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      aria-label={`${title}${isLastUsed ? ', last used' : ''}. ${description}`}
    >
      <HStack spacing={4} align="center">
        <Box
          w="44px"
          h="44px"
          borderRadius="lg"
          bg={tileBg}
          color={tileColor}
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
        >
          <Icon as={icon} boxSize="20px" aria-hidden="true" />
        </Box>
        <Box flex={1} minW={0}>
          <HStack spacing={2} align="center">
            <Text
              fontWeight="normal"
              fontSize="17px"
              color={titleColor}
              lineHeight="1.25"
            >
              {title}
            </Text>
            {isLastUsed && (
              <Badge
                bg={lastUsedBg}
                color={lastUsedColor}
                borderWidth="1px"
                borderColor={lastUsedBorder}
                fontSize="11px"
                fontWeight="normal"
                textTransform="uppercase"
                letterSpacing="0.04em"
                px={2}
                py={0.5}
                borderRadius="full"
                display="inline-flex"
                alignItems="center"
                gap={1}
                flexShrink={0}
              >
                <Icon as={TablerClock} boxSize="11px" aria-hidden="true" />
                Last used
              </Badge>
            )}
          </HStack>
          <Text color={subtleText} fontSize="15px" lineHeight="1.4" mt={0.5}>
            {description}
          </Text>
        </Box>
        <Icon
          as={TablerChevronRight}
          boxSize={5}
          color={chevronColor}
          flexShrink={0}
          aria-hidden="true"
        />
      </HStack>
    </Box>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      size={{base: 'full', md: 'md'}}
      motionPreset={motionPreset as any}
      scrollBehavior="inside"
    >
      <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
      <ModalContent
        bg={cardBg}
        borderRadius={{base: 0, md: '2xl'}}
        mx={{base: 0, md: 4}}
        my={{base: 0, md: 'auto'}}
        minH={{base: '100vh', md: 0}}
      >
        <ModalHeader pb={1} pt={{base: 5, md: 5}} px={{base: 5, md: 6}}>
          {step === 'language' ? (
            <HStack spacing={2} align="center" mb={1}>
              <Box
                as="button"
                type="button"
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                boxSize="28px"
                ml="-6px"
                borderRadius="lg"
                color={subtleText}
                transition="all 0.15s ease"
                _hover={{bg: optionHoverBg, color: titleColor}}
                _focusVisible={{
                  boxShadow: `0 0 0 3px ${focusRing}`,
                  outline: 'none',
                }}
                onClick={() => setStep('mode')}
                aria-label="Back to session type"
              >
                <Icon as={TablerChevronLeft} boxSize={5} aria-hidden="true" />
              </Box>
              <Text
                fontWeight="normal"
                fontSize={{base: '22px', md: '19px'}}
                color={titleColor}
              >
                What language?
              </Text>
            </HStack>
          ) : (
            <Text
              fontWeight="normal"
              fontSize={{base: '22px', md: '19px'}}
              color={titleColor}
            >
              {patientName ? (
                <>
                  Continue encounter with{' '}
                  <Text as="span" fontWeight="semibold" color={patientAccent}>
                    {patientName}
                  </Text>
                </>
              ) : (
                'Continue encounter'
              )}
            </Text>
          )}
          <Text color={subtleText} fontSize="15px" fontWeight="normal" mt={1}>
            {step === 'language'
              ? 'Pick the language for live translation. We\u2019ll save it to this encounter.'
              : 'Do you want the interpreter on for this session?'}
          </Text>
        </ModalHeader>
        <ModalCloseButton
          size="lg"
          top={{base: 5, md: 5}}
          right={{base: 5, md: 6}}
        />
        <ModalBody
          px={{base: 5, md: 6}}
          pb={{base: 'max(env(safe-area-inset-bottom), 24px)', md: 6}}
          pt={3}
        >
          {step === 'language' ? (
            <VStack spacing={5} align="stretch">
              <LanguageDialectPicker
                selectedLanguage={language}
                selectedDialect={dialect}
                onLanguageChange={(next: any) => {
                  setLanguage(next || '');
                  // A different language invalidates the previously picked
                  // dialect (dialects are language-specific).
                  setDialect('');
                }}
                onDialectChange={(next: any) => setDialect(next || '')}
                languageLabel="Language"
                dialectLabel="Dialect"
                isLanguageRequired
              />
              <Button
                type="button"
                variant="opalitePrimary"
                size="lg"
                w="100%"
                isDisabled={!(language || '').trim()}
                leftIcon={
                  <Icon as={TablerGlobe} boxSize="18px" aria-hidden="true" />
                }
                onClick={handleStartWithLanguage}
              >
                Start encounter
              </Button>
            </VStack>
          ) : (
            <VStack spacing={3} align="stretch">
              {renderOption({
                mode: LIVE_ENCOUNTER_MODE,
                title: 'Interpreter on',
                description: 'Live translation in another language.',
                icon: TablerGlobe,
                disabled: false,
                isLastUsed: lastUsedMode === LIVE_ENCOUNTER_MODE,
                tileBg: interpreterTileBg,
                tileColor: interpreterTileColor,
              })}
              {renderOption({
                mode: 'scribe',
                title: 'Interpreter off',
                description: scribeDisabled
                  ? 'Note generation is off for your account.'
                  : 'Record and generate a clinical note.',
                icon: TablerMicrophone,
                disabled: scribeDisabled,
                isLastUsed: lastUsedMode === 'scribe',
                tileBg: scribeTileBg,
                tileColor: scribeTileColor,
              })}
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ContinueModePickerModal;
